/**
 * 语言的选取与保存。
 *
 * 顺序：用户显式选过的 > 浏览器语言 > 中文。
 *
 * 存两份：localStorage 让没登录的人和首屏就能用对语言，账号偏好
 * （`users.preferences.language`）让**服务端**知道该用哪种语言发邮件 ——
 * 验证信和找回密码是在服务器上拼的，读不到浏览器里的 localStorage。
 * 两边不一致时以账号偏好为准（换了设备也应该是同一种语言）。
 */

import { BCP47, LOCALES } from './types'
import type { Locale } from './types'

export const LOCALE_STORAGE_KEY = 'rex:lang'

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

export function readStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    return isLocale(stored) ? stored : null
  } catch {
    // 隐私模式等场景下 localStorage 会抛
    return null
  }
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // 存不下就只对当前页面生效
  }
}

/**
 * 从浏览器的语言列表里挑第一个我们支持的。
 *
 * 只比较主语言子标签：zh-TW / zh-HK 也归到 zh —— 我们暂时只有简体，
 * 给繁体用户看简体，好过给他看英文。
 */
export function detectFromNavigator(): Locale | null {
  const candidates =
    typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language])
  for (const tag of candidates) {
    const primary = String(tag).toLowerCase().split('-')[0]
    if (isLocale(primary)) return primary
  }
  return null
}

export function detectLocale(): Locale {
  return readStoredLocale() ?? detectFromNavigator() ?? 'zh'
}

/** 让屏幕阅读器、浏览器的翻译提示、CJK 字形选择都跟着走。 */
export function applyDocumentLocale(locale: Locale): void {
  if (typeof document !== 'undefined') document.documentElement.lang = BCP47[locale]
}
